import { useState } from 'react';

import { Button as MuiButton, Stack, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import { say } from '@/james-playground/aa-family-claim/claim/asks';
import { text } from '@/james-playground/aa-family-claim/claim/facts';
import { SR1 } from '@/james-playground/aa-family-claim/claim/cards/cards.content';
import {
  CardShell,
  Note,
} from '@/james-playground/aa-family-claim/claim/cards/parts';
import { type CardProps } from '@/james-playground/aa-family-claim/claim/cards/types';

/**
 * The SR1 task on the quicker route: ask her doctor, nurse or consultant for
 * the form, with a message the family can copy and show or send. It never
 * holds the claim up, and the card says so.
 */
const Sr1Card = ({ ask, facts, voice: v, onSubmit, onSkip }: CardProps) => {
  const id = ask.entries[0];
  const [copied, setCopied] = useState(false);
  const name =
    [text(facts, 'firstNames'), text(facts, 'surname')]
      .filter(Boolean)
      .join(' ') || v.name;
  const message = SR1.message(name, text(facts, 'dob'));

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <CardShell
      label={say(ask.title, v)}
      onSkip={onSkip}
      actions={
        <>
          <MuiButton
            variant='contained'
            fullWidth
            onClick={() => onSubmit({ [id]: 'Yes' }, SR1.askedReply)}
          >
            {SR1.asked}
          </MuiButton>
          <MuiButton
            variant='outlined'
            fullWidth
            onClick={() => onSubmit({}, SR1.laterReply)}
          >
            {SR1.later}
          </MuiButton>
        </>
      }
    >
      <Typography variant='body2'>{SR1.body}</Typography>
      <Note>
        <Typography variant='caption'>{SR1.messageLabel}</Typography>
        <Typography variant='body1'>{message}</Typography>
      </Note>
      <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1.5}>
        <MuiButton
          variant='outlined'
          onClick={copy}
          startIcon={<Icon icon='copy' fontSize={14} />}
        >
          {SR1.copy}
        </MuiButton>
        <Typography variant='body2' role='status'>
          {copied ? SR1.copied : ''}
        </Typography>
      </Stack>
    </CardShell>
  );
};

Sr1Card.displayName = 'Sr1Card';
export default Sr1Card;
